import { useTranslation } from "react-i18next";
import { HOURS, RAMP, RAMP_TEXT, WEEKDAYS, heatLevel, hourLabel, localeOf, maxOf, weekdayLabels } from "../lib/heatmap";

// ---------------------------------------------------------------------------
// Mapa de calor 7 días × 24 horas (#537), al estilo del patrón semanal de patios
// hecho para Acuapue en Dumax (YardWeeklyHeatmap): grilla de divs y no recharts,
// porque son 168 celdas con foco de teclado y la columna de días fija con scroll
// horizontal es CSS. Cada celda es un botón: abre las horas y los commits que la
// justifican.
//
// #960: la celda cuenta FRANJAS (persona-hora de reloj con actividad): es
// evidencia de cuándo se trabaja, no horas pagadas. Las horas salen de los
// bloques de tiempo real, así que la celda ya no se rotula «h».
// ---------------------------------------------------------------------------

interface Props {
  grid: number[][];
  isDarkMode: boolean;
  selected?: { weekday: number; hour: number } | null;
  onSelect?: (cell: { weekday: number; hour: number; hours: number }) => void;
}

export default function WorkHoursHeatmap({ grid, isDarkMode, selected, onSelect }: Props) {
  const { t, i18n } = useTranslation("common");
  const theme = isDarkMode ? "dark" : "light";
  const max = maxOf(grid);
  // Nombres de día del idioma activo, siempre en la zona horaria de CDMX.
  const days = weekdayLabels(localeOf(i18n?.language));

  return (
    <div>
      <div className="overflow-x-auto">
        <div
          role="grid"
          aria-label={t("workHours.heatmap.title")}
          className="grid gap-[3px] min-w-[720px]"
          style={{ gridTemplateColumns: "44px repeat(24, minmax(24px, 1fr))" }}
        >
          <div />
          {HOURS.map((h) => (
            <div key={`h-${h}`} className={`text-center text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              {h % 3 === 0 ? String(h).padStart(2, "0") : ""}
            </div>
          ))}
          {WEEKDAYS.map((d) => (
            <div key={`row-${d}`} role="row" className="contents">
              <div className={`sticky left-0 flex items-center text-[11px] font-medium ${isDarkMode ? "text-gray-400 bg-dark-surface" : "text-gray-600 bg-white"}`}>
                {days[d]}
              </div>
              {HOURS.map((h) => {
                const value = grid[d]?.[h] ?? 0;
                const level = heatLevel(value, max);
                const isSelected = selected?.weekday === d && selected?.hour === h;
                return (
                  <button
                    key={`c-${d}-${h}`}
                    type="button"
                    role="gridcell"
                    aria-label={`${days[d]} ${hourLabel(h)}: ${t("workHours.heatmap.cell", { count: value })}`}
                    title={`${days[d]} ${hourLabel(h)} · ${t("workHours.heatmap.cell", { count: value })}`}
                    data-testid={`heat-${d}-${h}`}
                    onClick={() => onSelect?.({ weekday: d, hour: h, hours: value })}
                    className={`h-7 rounded-[4px] text-[10px] font-semibold transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-violet-400 ${
                      isSelected ? "ring-2 ring-violet-500" : ""
                    }`}
                    style={{ background: RAMP[theme][level], color: RAMP_TEXT[theme][level] }}
                  >
                    {value > 0 ? value : ""}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className={`mt-3 flex items-center gap-2 text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
        <span>{t("workHours.heatmap.less")}</span>
        {RAMP[theme].map((color, i) => (
          <span key={color} className="h-3 w-5 rounded-sm" style={{ background: color }} title={i === 0 ? t("workHours.heatmap.cell", { count: 0 }) : undefined} />
        ))}
        <span>{t("workHours.heatmap.more")}</span>
        <span className="ml-auto">{t("workHours.heatmap.legend")}</span>
      </div>
    </div>
  );
}
